import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserByEmail, logout } from '../../store/actions/userActions';
import { COACH_VERIFICATION_PATH } from '../ForCoaches/v2/coachPaths';
import { Btn, ErrorState, Loading } from '../ForBrands/ui/BrandUI';
import '../ForBrands/brandMode.css';

// Kept for the callers that import the awaiting path from the guard.
export { COACH_VERIFICATION_PATH };

// Athletes and coaches must finish the onboarding wizard before they can hit
// any other protected page. Other roles bypass this gate entirely.
const ROLES_REQUIRING_ONBOARDING = ['IS_ATHLETE', 'IS_COACH'];

// Routes a coach can open while awaiting admin verification: the awaiting
// screen, their own profile, billing (it opens no team data, so a coach may
// subscribe before verification), password and the onboarding wizard (to
// change the submitted team). Every other route, including Home, Team,
// CacheShare™ and both Circle pages, leads to the awaiting screen, so an
// unverified coach never sees athlete emails, rosters or private data.
const COACH_PENDING_ALLOWED_PREFIXES = [
  COACH_VERIFICATION_PATH,
  '/coach/profile',
  '/coach/billing',
  '/coach/password',
  '/onboarding',
];

const onboardingDoneFor = (user) => !!(user?.onboardingCompleted || user?.options?.onboardingCompleted);

// A coach who finished onboarding but is not admin-verified (pending,
// rejected or never reviewed). The Coach Mode shell uses the same test to
// lock its team tabs.
export const awaitingCoachVerification = (user) => (
  (user?.permissions || []).includes('IS_COACH')
  && user?.coachVerification?.status !== 'verified'
  && onboardingDoneFor(user)
);

const ProtectedRoute = ({
  children, permissions, subscriptionRequired,
  skipOnboardingGate, skipCoachVerificationGate,
}) => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user || {});
  const loadError = useSelector((state) => state.user.loadError);
  const userPermissions = user.permissions || [];
  const subscriptionStatus = user?.subscription?.status || "";

  // ⛔ No token → redirect to login immediately
  if (!token) {
    return <Navigate to="/login" />;
  }

  // ⏳ If token exists but permissions haven't loaded yet, show the V2
  // loading line.
  // When the load failed (network or server trouble), offer a retry and a
  // way to log out instead of spinning forever. A refused session (locked or
  // deleted account) is signed out by fetchUserByEmail itself.
  if (token && !userPermissions.length) {
    if (loadError) {
      const retry = () => {
        const email = localStorage.getItem('myEmail');
        if (email) dispatch(fetchUserByEmail(email));
      };
      const signOut = () => {
        dispatch(logout());
        navigate('/login', { replace: true });
      };
      return (
        <div className="bm-root flex w-full justify-center bg-white px-4">
          <div role="alert" className="mt-10 flex w-full max-w-[480px] flex-col items-center gap-3 text-center">
            <ErrorState>{loadError}</ErrorState>
            <div className="flex gap-2">
              <Btn onClick={retry}>Try again</Btn>
              <Btn variant="secondary" onClick={signOut}>Log out</Btn>
            </div>
          </div>
        </div>
      );
    }
    return <div className="bm-root w-full bg-white"><Loading /></div>;
  }

  // ❌ Token exists, but doesn't have the required permissions → redirect
  const hasPermission = permissions.some(permission => userPermissions.includes(permission));
  if (!hasPermission) {
    return <Navigate to="/unauthorized" />; // or "/login"
  }

  if (subscriptionRequired && !subscriptionRequired.includes(subscriptionStatus)) {
    return <Navigate to="/unauthorized" />;
  }

  // 🛂 Onboarding gate: athletes/coaches who haven't finished the wizard get
  // bounced to /onboarding. The wizard route itself sets skipOnboardingGate
  // so we never loop on it.
  const needsOnboarding = userPermissions.some((p) => ROLES_REQUIRING_ONBOARDING.includes(p));
  if (
    !skipOnboardingGate &&
    needsOnboarding &&
    !onboardingDoneFor(user) &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // 🪪 Coach-verification gate: a coach who finished onboarding but is not
  // admin-verified yet lands on the awaiting screen from any route outside
  // the allow-list above.
  if (
    !skipCoachVerificationGate &&
    awaitingCoachVerification(user) &&
    !COACH_PENDING_ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to={COACH_VERIFICATION_PATH} replace />;
  }

  // ✅ All good
  return children;
};

export default ProtectedRoute;
